import { AnimatePresence, motion, type Variants } from "motion/react";
import type React from "react";
import { ExampleTag, shotProps } from "@/components/blocks/frames/Frames";
import { EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import type { Locale } from "@/lib/i18n/locale";
import { cn } from "@/lib/utils";

/** Percentages with the French no-break space, identical on server and client. */
export const formatPercent = (value: number, locale: Locale) =>
	locale.startsWith("fr")
		? `${String(value).replace(".", ",")} %`
		: `${value}%`;

/** Props shared by the four use-case scenes. */
export type UseCaseSceneProps = {
	/** Pins the scene on one step (sticky story); cycles on its own otherwise. */
	stage?: number;
	/** Shows the step rail under the scene. */
	steps?: boolean;
	className?: string;
};

/*
 * Step content animates through variants inherited from the keyed panel, so
 * `AnimatePresence initial={false}` server-renders the finished state.
 */
const panel: Variants = {
	hidden: { opacity: 0, y: 12, scale: 0.985 },
	visible: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: { duration: 0.45, ease: EASE },
	},
	exit: {
		opacity: 0,
		y: -10,
		scale: 0.985,
		transition: { duration: 0.25, ease: EASE },
	},
};

/** A row sliding in, staggered by `custom` index. */
export const stageItem: Variants = {
	hidden: { opacity: 0, x: -8 },
	visible: (i = 0) => ({
		opacity: 1,
		x: 0,
		transition: { duration: 0.4, ease: EASE, delay: 0.12 + i * 0.12 },
	}),
};

/** A block rising in, staggered by `custom` index. */
export const stageRise: Variants = {
	hidden: { opacity: 0, y: 10 },
	visible: (i = 0) => ({
		opacity: 1,
		y: 0,
		transition: { duration: 0.45, ease: EASE, delay: 0.12 + i * 0.12 },
	}),
};

/** A bar filling from the left, staggered by `custom` index. */
export const stageBar: Variants = {
	hidden: { scaleX: 0 },
	visible: (i = 0) => ({
		scaleX: 1,
		transition: { duration: 0.8, ease: EASE, delay: 0.25 + i * 0.15 },
	}),
};

/** A check or badge popping in, delayed by `custom` seconds. */
export const stagePop: Variants = {
	hidden: { scale: 0, opacity: 0 },
	visible: (delay = 0.6) => ({
		scale: 1,
		opacity: 1,
		transition: { ...SPRING, delay },
	}),
};

/** A stroke drawing itself, delayed by `custom` seconds. */
export const stageDraw: Variants = {
	hidden: { pathLength: 0 },
	visible: (delay = 0.3) => ({
		pathLength: 1,
		transition: { duration: 1.2, ease: EASE, delay },
	}),
};

/**
 * Four steps of one trade's workflow on a single stage: each step swaps in
 * its own product screen (browser, phone or document). Controlled by `stage`
 * or cycling while on screen; the server renders `initial`.
 */
export function StageScene({
	stage,
	steps = true,
	labels,
	durations,
	initial,
	render,
	className,
}: UseCaseSceneProps & {
	labels: readonly string[];
	durations: readonly number[];
	initial: number;
	render: (step: number) => React.ReactNode;
}) {
	const loop = useSceneLoop(durations, {
		initial,
		enabled: stage === undefined,
	});
	const current = stage ?? loop.index;

	return (
		<div ref={loop.ref} className={cn("w-full", className)}>
			<div className="relative aspect-[4/5] w-full sm:aspect-[6/5]">
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={current}
						className="absolute inset-0 flex items-center justify-center"
						variants={panel}
						initial="hidden"
						animate="visible"
						exit="exit"
					>
						{render(current)}
					</motion.div>
				</AnimatePresence>
			</div>
			{steps ? (
				<ol
					aria-hidden="true"
					className="mt-6 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-4"
				>
					{labels.map((label, index) => (
						<li key={label} className="min-w-0">
							<span className="relative block h-0.5 overflow-hidden rounded-full bg-foreground/10">
								<motion.span
									className="absolute inset-0 origin-left bg-signal"
									initial={false}
									animate={{ scaleX: index <= current ? 1 : 0 }}
									transition={{ duration: 0.6, ease: EASE }}
								/>
							</span>
							<span
								className={cn(
									"mt-2.5 block truncate font-mono text-[10px] tracking-[0.04em] transition-colors duration-500",
									index === current
										? "text-foreground"
										: "text-muted-foreground",
								)}
							>
								<span className="mr-1.5 text-signal-ink">0{index + 1}</span>
								{label}
							</span>
						</li>
					))}
				</ol>
			) : null}
		</div>
	);
}

/** A printed A4 page, light product theme, for generated documents. */
export function PaperSheet({
	tag,
	children,
	className,
}: {
	tag: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div
			{...shotProps}
			className={cn(
				"paper shot-shadow relative aspect-[1/1.3] h-full max-h-full overflow-hidden rounded-md border select-none",
				className,
			)}
		>
			<ExampleTag className="absolute top-3 right-3">{tag}</ExampleTag>
			{children}
		</div>
	);
}
